import WarningAmberOutlined from "@mui/icons-material/WarningAmberOutlined";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@bw/shared/hooks";
import { isInternalPlatformEmail } from "../platformAccess";

export function AccessDeniedPage() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [signingOut, setSigningOut] = useState(false);

  const displayName = useMemo(() => {
    if (user?.displayName?.trim()) return user.displayName.trim();
    if (user?.email?.trim()) return user.email.trim();
    return "This account";
  }, [user?.displayName, user?.email]);
  const isInternalEmployee = isInternalPlatformEmail(user?.email);

  async function handleSignOut() {
    if (signingOut) return;
    setSigningOut(true);
    try {
      await signOut();
    } finally {
      navigate("/login", { replace: true });
      setSigningOut(false);
    }
  }

  return (
    <Box
      component="main"
      sx={{
        display: "flex",
        minHeight: "100dvh",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "background.default",
        px: 2,
        py: 6,
      }}
    >
      <Paper
        variant="outlined"
        sx={{ width: "100%", maxWidth: 560, p: { xs: 3, sm: 4 } }}
      >
        <Stack spacing={3} sx={{ alignItems: "flex-start" }}>
          <WarningAmberOutlined color="warning" fontSize="large" />
          <Box>
            <Typography component="h1" variant="h5" sx={{ fontWeight: 700 }}>
              Platform access required
            </Typography>
            <Typography color="text.secondary" sx={{ mt: 1.5 }}>
              {displayName} authenticated successfully, but platform access
              requires both an internal @badmintonwarriors.com identity and
              membership in admins@badmintonwarriors.com.
            </Typography>
          </Box>
          <Alert severity="info" sx={{ width: "100%" }}>
            {isInternalEmployee
              ? "Ask an existing platform admin to add this exact employee account to admins@badmintonwarriors.com."
              : "Use your @badmintonwarriors.com Google account instead of a personal or external identity."}{" "}
            After access is granted, sign out and sign back in to refresh the
            session.
          </Alert>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <Button
              variant="contained"
              onClick={() => void handleSignOut()}
              disabled={signingOut}
            >
              {signingOut ? "Signing out…" : "Sign out"}
            </Button>
            <Button
              component="a"
              href="mailto:platform@badmintonwarriors.com"
              variant="outlined"
            >
              Contact platform team
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </Box>
  );
}
